import { triggerBlobDownload } from './download.util';

/**
 * One column of an exported file: the header it is written under, and what it
 * reads out of a row.
 *
 * A function rather than a field name so a column can be something the row
 * does not literally hold - a percentage the screen shows as "25%", a label
 * looked up from a catalogue, two fields joined into one cell.
 */
export interface CsvColumn<T> {
  header: string;
  cell: (row: T) => string | number | null | undefined;
}

/**
 * The rows and columns as CSV text, ready to be a file.
 *
 * Separate from the download below so a test can read what would be written
 * without a browser having to save anything.
 */
export function csvText<T>(
  columns: readonly CsvColumn<T>[],
  rows: readonly T[],
): string {
  // Quote-escape AND neutralize spreadsheet formula injection: a cell that
  // starts with = + - @ (e.g. an imported name "=HYPERLINK(...)") is prefixed
  // with a quote so Excel treats it as text, not a formula.
  const escape = (value: string | number | null | undefined) => {
    let text = String(value ?? '');
    if (/^[=+\-@]/.test(text)) text = `'${text}`;
    return `"${text.replace(/"/g, '""')}"`;
  };
  // Headers are quoted ONLY when the label forces it. An importer's signature
  // is the first header cell read as text, and a person opening the file
  // should see `LeadId,Title,Address,...` rather than a row of quoted strings
  // - but a label that ever contains a comma still has to survive the trip.
  const headerCell = (label: string) =>
    /[",\r\n]/.test(label) ? `"${label.replace(/"/g, '""')}"` : label;
  const header = columns.map(column => headerCell(column.header)).join(',');
  const lines = rows.map(row =>
    columns.map(column => escape(column.cell(row))).join(','));
  // BOM so Excel on Windows reads UTF-8 (not ANSI); CRLF per RFC 4180.
  return '﻿' + [header, ...lines].join('\r\n');
}

/**
 * Writes the rows out as a file the browser downloads.
 *
 * Handed to the BROWSER to download, not written to disk by the page.
 *
 * Export used to prefer the File System Access API's save picker where it
 * existed, so that somebody could choose the folder. It works, and it has one
 * consequence nobody weighed: a page that writes through a file handle
 * performs no download, so the browser records none. Nothing appears in the
 * downloads bubble, nothing is in the download history, and there is no "open
 * containing folder" to go back to. The file lands somewhere the browser
 * cannot tell you about afterwards - and on Chromium in a secure context,
 * which localhost is, that was every export.
 *
 * An ordinary download gives that up: it goes to the download folder and the
 * person does not pick. What it buys is that the export behaves like every
 * other file the browser has ever given them - it appears in the bubble as it
 * arrives, it stays in the history, and it can be reopened from there. Being
 * findable afterwards beats being placed precisely once.
 */
export function downloadCsv<T>(
  columns: readonly CsvColumn<T>[],
  rows: readonly T[],
  filename: string,
): void {
  const blob = new Blob([csvText(columns, rows)], {
    type: 'text/csv;charset=utf-8',
  });
  triggerBlobDownload(blob, filename);
}
